import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const CashOutResultDialog = ({
  open,
  onClose,
  result,
  formatCurrency,
  locationCurrency,
}) => {
  const { t } = useTranslation("dashboard");
  if (!result) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            textTransform: "lowercase",
            color: "#7c28d6",
            textDecoration: "underline",
          }}
        >
          {t("walletCashOut")}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "#666" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: "center" }}>
          {/* Success/Error Icon */}
          <Box
            sx={{
              width: { xs: 80, sm: 100 },
              height: { xs: 80, sm: 100 },
              borderRadius: "50%",
              bgcolor: result.success ? "#4caf50" : "#f44336",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            {result.success ? (
              <CheckCircleIcon
                sx={{
                  color: "#fff",
                  fontSize: { xs: 50, sm: 60 },
                }}
              />
            ) : (
              <CancelIcon
                sx={{
                  color: "#fff",
                  fontSize: { xs: 50, sm: 60 },
                }}
              />
            )}
          </Box>

          {/* Cash Out Amount */}
          <Typography
            variant="body1"
            sx={{
              color: "#222",
              mb: 1,
              textTransform: "lowercase",
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            {t("walletCashOutAmount", { amount: formatCurrency(result.amount * 100, locationCurrency) })}
          </Typography>

          {/* Success Message */}
          {result.success && (
            <Typography
              variant="h6"
              sx={{
                color: "#4caf50",
                fontWeight: 600,
                textTransform: "lowercase",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              {t("walletCashOutCompleted")}
            </Typography>
          )}

          {/* Error Message */}
          {!result.success && (
            <Box>
              <Typography
                variant="body1"
                sx={{
                  color: "#222",
                  mb: 1,
                  textTransform: "lowercase",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                {t("walletCashOutFailed")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#f44336",
                  textTransform: "lowercase",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                {result.errorMessage}
              </Typography>
            </Box>
          )}

          {result.cashoutFeePercentage > 0 && (
            <Typography
              variant="caption"
              sx={{
                mt: 2,
                color: "#666",
                textTransform: "lowercase",
                display: "block",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              {t("walletCashOutFeeNote", { percent: result.cashoutFeePercentage })}
          </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CashOutResultDialog;
